import { useMemo, useState } from 'react';
import { Check, ChevronLeft, ChevronRight, HeartPulse, Info } from 'lucide-react';

export interface MLHFQResultPayload {
  answers: number[];
  total: number;
  maxScore: number;
  physical: { score: number; maxScore: number; items: number[] };
  emotional: { score: number; maxScore: number; items: number[] };
  other: { score: number; maxScore: number; items: number[] };
  finishedAt?: string;
}

interface MLHFQTestProps {
  initial?: number[];
  onCancel?: () => void;
  onNext: (result: MLHFQResultPayload) => void;
}

export const MLHFQ_ITEMS_PT: string[] = [
  'Causou inchaço em seus tornozelos e pernas?',
  'Obrigando você a sentar ou deitar para descansar durante o dia?',
  'Tornando sua caminhada e subida de escadas difíceis?',
  'Tornando seu trabalho doméstico difícil?',
  'Tornando suas saídas de casa difíceis?',
  'Tornando difícil dormir bem à noite?',
  'Tornando seus relacionamentos ou atividades com familiares e amigos difíceis?',
  'Tornando seu trabalho para ganhar a vida difícil?',
  'Tornando seus passatempos, esportes e diversão difíceis?',
  'Tornando sua atividade sexual difícil?',
  'Fazendo você comer menos as comidas que você gosta?',
  'Causando falta de ar?',
  'Deixando você cansado, fatigado ou com pouca energia?',
  'Obrigando você a ficar hospitalizado?',
  'Fazendo você gastar dinheiro com cuidados médicos?',
  'Causando a você efeitos colaterais das medicações?',
  'Fazendo você sentir-se um peso para familiares e amigos?',
  'Fazendo você sentir uma falta de autocontrole na sua vida?',
  'Fazendo você se preocupar?',
  'Tornando difícil você se concentrar ou lembrar-se das coisas?',
  'Fazendo você sentir-se deprimido(a)?',
];

export const MLHFQ_PHYSICAL_1BASED = [2, 3, 4, 5, 6, 7, 12, 13] as const;
export const MLHFQ_EMOTIONAL_1BASED = [17, 18, 19, 20, 21] as const;
export const MLHFQ_OTHER_1BASED = MLHFQ_ITEMS_PT.map((_, index) => index + 1).filter(
  (itemNumber) => !MLHFQ_PHYSICAL_1BASED.includes(itemNumber as (typeof MLHFQ_PHYSICAL_1BASED)[number]) &&
    !MLHFQ_EMOTIONAL_1BASED.includes(itemNumber as (typeof MLHFQ_EMOTIONAL_1BASED)[number]),
);

export const MLHFQ_OPTIONS = [
  { value: 0, label: '0 — Não' },
  { value: 1, label: '1 — Muito pouco' },
  { value: 2, label: '2 — Pouco' },
  { value: 3, label: '3 — Moderadamente' },
  { value: 4, label: '4 — Muito' },
  { value: 5, label: '5 — Demais' },
] as const;

export default function MLHFQTest({ initial, onCancel, onNext }: MLHFQTestProps) {
  const [answers, setAnswers] = useState<number[]>(initial ?? Array(MLHFQ_ITEMS_PT.length).fill(-1));
  const [step, setStep] = useState(0);

  const allAnswered = answers.every((value) => value >= 0);

  const total = useMemo(
    () => answers.reduce((sum, value) => sum + (value >= 0 ? value : 0), 0),
    [answers],
  );

  const sumBy1Based = (itemNumbers: readonly number[]) =>
    itemNumbers.reduce((sum, itemNumber) => {
      const answer = answers[itemNumber - 1];
      return sum + (answer >= 0 ? answer : 0);
    }, 0);

  const physicalScore = sumBy1Based(MLHFQ_PHYSICAL_1BASED);
  const emotionalScore = sumBy1Based(MLHFQ_EMOTIONAL_1BASED);
  const otherScore = sumBy1Based(MLHFQ_OTHER_1BASED);

  const setAnswer = (index: number, value: number) => {
    setAnswers((previous) => {
      const next = [...previous];
      next[index] = value;
      return next;
    });
  };

  return (
    <div className="space-y-4">
      <div className="flex items-start gap-2 rounded-md bg-blue-50 p-3 text-blue-900">
        <Info className="mt-0.5 h-5 w-5" />
        <div>
          <p className="font-medium">MLHFQ — Minnesota Living with Heart Failure Questionnaire</p>
          <p className="text-sm opacity-90">
            Período de referência: <strong>último mês</strong>. Marque de 0 a 5 quanto a insuficiência cardíaca impactou sua qualidade de vida.
          </p>
        </div>
      </div>

      <div className="grid gap-3 md:grid-cols-4">
        <div className="rounded-xl border border-blue-100 bg-blue-50 p-4 md:col-span-2">
          <div className="flex items-center gap-2 text-blue-800">
            <HeartPulse className="h-4 w-4" />
            <span className="text-xs font-black uppercase tracking-[0.25em]">Progresso</span>
          </div>
          <div className="mt-2 text-lg font-semibold text-slate-900">Questão {step + 1} de {MLHFQ_ITEMS_PT.length}</div>
          <div className="mt-3 h-2 rounded-full bg-white/80">
            <div
              className="h-2 rounded-full bg-blue-600 transition-all"
              style={{ width: `${((step + 1) / MLHFQ_ITEMS_PT.length) * 100}%` }}
            />
          </div>
        </div>
        <div className="rounded-xl border border-slate-200 bg-slate-50 p-4">
          <div className="text-[11px] font-black uppercase tracking-[0.22em] text-slate-500">Pontuação total</div>
          <div className="mt-2 text-2xl font-black text-slate-900">{total}/105</div>
        </div>
        <div className="rounded-xl border border-slate-200 bg-slate-50 p-4 text-sm text-slate-700">
          <div>Físico: <span className="font-semibold text-slate-900">{physicalScore}/40</span></div>
          <div className="mt-1">Emocional: <span className="font-semibold text-slate-900">{emotionalScore}/25</span></div>
          <div className="mt-1">Outros: <span className="font-semibold text-slate-900">{otherScore}/40</span></div>
        </div>
      </div>

      <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
        <div className="mb-4 flex items-center justify-between gap-3">
          <div>
            <div className="text-sm font-semibold text-slate-900">Item {step + 1}</div>
            <div className="mt-1 text-xs font-medium uppercase tracking-[0.2em] text-slate-500">Qualidade de vida na insuficiência cardíaca</div>
          </div>
          <div className="text-sm text-slate-500">Selecionado: {answers[step] >= 0 ? answers[step] : '—'}</div>
        </div>

        <div className="mb-4 rounded-2xl border border-slate-200 bg-slate-50 p-4">
          <p className="text-base font-medium leading-relaxed text-slate-900">{MLHFQ_ITEMS_PT[step]}</p>
        </div>

        <div className="grid grid-cols-1 gap-2 md:grid-cols-2 xl:grid-cols-3">
          {MLHFQ_OPTIONS.map((option) => (
            <label
              key={option.value}
              className={`flex cursor-pointer items-center gap-3 rounded-xl border px-4 py-3 text-sm transition ${
                answers[step] === option.value
                  ? 'border-blue-600 bg-blue-600 text-white'
                  : 'bg-white text-slate-700 hover:border-slate-300 hover:bg-slate-50'
              }`}
            >
              <input
                type="radio"
                name={`mlhfq_v2_${step}`}
                className="hidden"
                checked={answers[step] === option.value}
                onChange={() => setAnswer(step, option.value)}
              />
              <span className="flex h-6 w-6 items-center justify-center rounded-full border border-current text-xs font-bold">
                {option.value}
              </span>
              <span className="flex-1">{option.label}</span>
            </label>
          ))}
        </div>

        <div className="mt-5 flex items-center justify-between">
          <button
            type="button"
            className="inline-flex items-center rounded-md bg-gray-100 px-3 py-2 text-sm font-medium text-slate-700 hover:bg-gray-200 disabled:opacity-50"
            onClick={() => setStep((current) => Math.max(0, current - 1))}
            disabled={step === 0}
          >
            <ChevronLeft className="mr-1 h-4 w-4" />
            Anterior
          </button>

          {step < MLHFQ_ITEMS_PT.length - 1 ? (
            <button
              type="button"
              className="inline-flex items-center rounded-md bg-blue-600 px-3 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50"
              onClick={() => setStep((current) => Math.min(MLHFQ_ITEMS_PT.length - 1, current + 1))}
              disabled={answers[step] < 0}
            >
              Próxima
              <ChevronRight className="ml-1 h-4 w-4" />
            </button>
          ) : (
            <button
              type="button"
              className="inline-flex items-center rounded-md bg-green-600 px-3 py-2 text-sm font-medium text-white hover:bg-green-700 disabled:opacity-50"
              disabled={!allAnswered}
              onClick={() =>
                allAnswered &&
                onNext({
                  answers,
                  total,
                  maxScore: 105,
                  physical: { score: physicalScore, maxScore: MLHFQ_PHYSICAL_1BASED.length * 5, items: [...MLHFQ_PHYSICAL_1BASED] },
                  emotional: { score: emotionalScore, maxScore: MLHFQ_EMOTIONAL_1BASED.length * 5, items: [...MLHFQ_EMOTIONAL_1BASED] },
                  other: { score: otherScore, maxScore: MLHFQ_OTHER_1BASED.length * 5, items: [...MLHFQ_OTHER_1BASED] },
                  finishedAt: new Date().toISOString(),
                })
              }
            >
              Concluir MLHFQ
              <Check className="ml-1 h-4 w-4" />
            </button>
          )}
        </div>
      </div>

      <div className="flex items-center justify-between">
        <button type="button" className="text-sm text-gray-600 hover:text-gray-800" onClick={onCancel}>
          Cancelar
        </button>
        <div className="text-right text-sm text-gray-700">
          <div>Total: {total}/105</div>
          <div>Físico {physicalScore}/40 • Emocional {emotionalScore}/25</div>
        </div>
      </div>
    </div>
  );
}
